import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminNav from '../components/admin/AdminNav';
import fixEncoding from '../utils/fixEncoding';
import CategoryIcon from '../components/CategoryIcon';
import categoryImageSrc from '../utils/categoryImageSrc';
import '../styles/pages/AdminCategoriesPage.scss';

const API_URL = process.env.REACT_APP_API_URL;

const AdminCategoriesPage = () => {
  const getCatImageSrc = categoryImageSrc;

  const [categories, setCategories] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');

  // Galería de referencia
  const [showGallery, setShowGallery] = useState(false);

  // Edición / creación
  const [editingCatId,   setEditingCatId]   = useState(null);
  const [editingCatName, setEditingCatName] = useState('');
  const [editingSubId,   setEditingSubId]   = useState(null);
  const [editingSubName, setEditingSubName] = useState('');

  const [newCatName,       setNewCatName]       = useState('');
  const [newCatImage,      setNewCatImage]      = useState(null);
  const [newCatImagePreview, setNewCatImagePreview] = useState('');
  const [addingSubToCatId, setAddingSubToCatId] = useState(null);
  const [newSubName,       setNewSubName]        = useState('');
  const [showNewCatForm,   setShowNewCatForm]   = useState(false);
  const [search,           setSearch]           = useState('');

  // Edición de imagen de categoría existente
  const [editingCatImage,       setEditingCatImage]       = useState(null);
  const [editingCatImagePreview, setEditingCatImagePreview] = useState('');

  // Modal de reubicación
  // { type: 'cat'|'sub', id, name, productCount }
  const [reubicateModal,  setReubicateModal]  = useState(null);
  const [reubicateTarget, setReubicateTarget] = useState('');
  const [reubicating,     setReubicating]     = useState(false);

  const getAuthHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem('admToken')}`,
  });

  const notify = (msg, isError = false) => {
    if (isError) setError(msg); else setSuccess(msg);
    setTimeout(() => { setError(''); setSuccess(''); }, 3500);
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/products/taxonomies`, {
        headers: getAuthHeader(),
      });
      setCategories(res.data);
    } catch {
      notify('Error al cargar categorías.', true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCategories(); }, []);

  // ── Categorías ────────────────────────────────────────────

  const handleImageChange = (file, setImage, setPreview) => {
    if (!file) return;
    setImage(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleCreateCat = async (e) => {
    e.preventDefault();
    if (!newCatName.trim()) return;
    try {
      const formData = new FormData();
      formData.append('name', newCatName.trim());
      if (newCatImage) formData.append('image', newCatImage);
      await axios.post(`${API_URL}/admin/categories`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });
      notify('Categoría creada.');
      setNewCatName('');
      setNewCatImage(null);
      setNewCatImagePreview('');
      setShowNewCatForm(false);
      fetchCategories();
    } catch (err) {
      notify(err.response?.data?.message || 'Error al crear categoría.', true);
    }
  };

  const handleRenameCat = async (id) => {
    if (!editingCatName.trim()) return;
    try {
      const formData = new FormData();
      formData.append('name', editingCatName.trim());
      if (editingCatImage) formData.append('image', editingCatImage);
      await axios.put(`${API_URL}/admin/categories/${id}`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });
      notify('Categoría actualizada.');
      setEditingCatId(null);
      setEditingCatImage(null);
      setEditingCatImagePreview('');
      fetchCategories();
    } catch (err) {
      notify(err.response?.data?.message || 'Error al renombrar.', true);
    }
  };

  const handleDeleteCat = async (id, name) => {
    try {
      await axios.delete(`${API_URL}/admin/categories/${id}`, { headers: getAuthHeader() });
      notify('Categoría eliminada.');
      fetchCategories();
    } catch (err) {
      if (err.response?.status === 409) {
        const productCount = err.response.data?.productCount ?? '?';
        setReubicateModal({ type: 'cat', id, name, productCount });
        setReubicateTarget('');
      } else {
        notify(err.response?.data?.message || 'Error al eliminar.', true);
      }
    }
  };

  // ── Subcategorías ─────────────────────────────────────────

  const handleCreateSub = async (e, catId) => {
    e.preventDefault();
    if (!newSubName.trim()) return;
    try {
      await axios.post(`${API_URL}/admin/categories/${catId}/subcategories`, { name: newSubName.trim() }, { headers: getAuthHeader() });
      notify('Subcategoría creada.');
      setNewSubName('');
      setAddingSubToCatId(null);
      fetchCategories();
    } catch (err) {
      notify(err.response?.data?.message || 'Error al crear subcategoría.', true);
    }
  };

  const handleRenameSub = async (id) => {
    if (!editingSubName.trim()) return;
    try {
      await axios.put(`${API_URL}/admin/subcategories/${id}`, { name: editingSubName.trim() }, { headers: getAuthHeader() });
      notify('Subcategoría actualizada.');
      setEditingSubId(null);
      fetchCategories();
    } catch (err) {
      notify(err.response?.data?.message || 'Error al renombrar.', true);
    }
  };

  const handleDeleteSub = async (id, name) => {
    try {
      await axios.delete(`${API_URL}/admin/subcategories/${id}`, { headers: getAuthHeader() });
      notify('Subcategoría eliminada.');
      fetchCategories();
    } catch (err) {
      if (err.response?.status === 409) {
        const productCount = err.response.data?.productCount ?? '?';
        setReubicateModal({ type: 'sub', id, name, productCount });
        setReubicateTarget('');
      } else {
        notify(err.response?.data?.message || 'Error al eliminar.', true);
      }
    }
  };

  // ── Reubicar y eliminar ───────────────────────────────────

  const handleReubicateAndDelete = async () => {
    if (!reubicateTarget) return;
    setReubicating(true);
    try {
      const { type, id } = reubicateModal;
      const targetId = Number(reubicateTarget);

      if (type === 'cat') {
        await axios.put(
          `${API_URL}/admin/categories/${id}/reassign-products`,
          { target_category_id: targetId },
          { headers: getAuthHeader() },
        );
        await axios.delete(`${API_URL}/admin/categories/${id}`, { headers: getAuthHeader() });
        notify('Productos reubicados y categoría eliminada.');
      } else {
        await axios.put(
          `${API_URL}/admin/subcategories/${id}/reassign-products`,
          { target_subcategory_id: targetId },
          { headers: getAuthHeader() },
        );
        await axios.delete(`${API_URL}/admin/subcategories/${id}`, { headers: getAuthHeader() });
        notify('Productos reubicados y subcategoría eliminada.');
      }

      setReubicateModal(null);
      fetchCategories();
    } catch (err) {
      notify(err.response?.data?.message || 'Error al reubicar.', true);
    } finally {
      setReubicating(false);
    }
  };

  // ── Opciones destino para el modal ────────────────────────

  const destinationOptions = () => {
    if (!reubicateModal) return [];
    if (reubicateModal.type === 'cat') {
      return categories
        .filter((c) => c.id !== reubicateModal.id)
        .sort((a, b) => a.name.localeCompare(b.name, 'es'));
    }
    // subcategoría → listar todas las subcategorías de otras categorías
    return categories.flatMap((c) =>
      (c.subcategories || [])
        .filter((s) => s.id !== reubicateModal.id)
        .map((s) => ({ id: s.id, label: `${fixEncoding(c.name)} › ${fixEncoding(s.name)}` })),
    ).sort((a, b) => a.label.localeCompare(b.label, 'es'));
  };

  // ─────────────────────────────────────────────────────────

  if (loading) return (
    <div className="acp">
      <AdminNav />
      <p className="acp__loading">Cargando categorías...</p>
    </div>
  );

  return (
    <div className="acp">
      <header className="acp__header">
        <div>
          <h1>Gestión de categorías</h1>
          <p className="acp__subtitle">{categories.length} categorías</p>
        </div>
      </header>

      <AdminNav />

      <div className="acp__toolbar">
        <input
          className="acp-input acp__search"
          placeholder="Buscar categoría..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button
          type="button"
          className={`acp-btn acp-btn--outline acp-btn--sm${showGallery ? ' acp-btn--active' : ''}`}
          onClick={() => setShowGallery(v => !v)}
          title="Ver imágenes de todas las categorías"
        >
          {showGallery ? 'Ocultar imágenes' : '🖼 Ver imágenes'}
        </button>
        <button className="acp-btn acp-btn--primary" onClick={() => setShowNewCatForm(true)}>
          + Nueva categoría
        </button>
      </div>

      {/* Galería de referencia */}
      {showGallery && (
        <div className="acp-gallery">
          <p className="acp-gallery__title">Imágenes actuales de categorías</p>
          <div className="acp-gallery__grid">
            {categories
              .slice()
              .sort((a, b) => a.name.localeCompare(b.name, 'es'))
              .map((cat) => {
                return (
                  <div key={cat.id} className="acp-gallery__item">
                    <CategoryIcon slug={cat.slug} imageUrl={getCatImageSrc(cat)} className="acp-gallery__img" />
                    <span className="acp-gallery__name">{fixEncoding(cat.name)}</span>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {error   && <div className="acp-alert acp-alert--error">{error}</div>}
      {success && <div className="acp-alert acp-alert--success">{success}</div>}

      {/* Formulario nueva categoría */}
      {showNewCatForm && (
        <form className="acp-new-form" onSubmit={handleCreateCat}>
          <div className="acp-new-form__fields">
            <input
              autoFocus
              className="acp-input"
              placeholder="Nombre de la nueva categoría"
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
            />
            <label className="acp-img-upload">
              {newCatImagePreview
                ? <img src={newCatImagePreview} alt="preview" className="acp-img-upload__preview" />
                : <span className="acp-img-upload__placeholder">+ Imagen (SVG / PNG)</span>
              }
              <input
                type="file"
                accept="image/svg+xml,image/png,image/jpeg,image/webp"
                style={{ display: 'none' }}
                onChange={(e) => handleImageChange(e.target.files[0], setNewCatImage, setNewCatImagePreview)}
              />
            </label>
          </div>
          <div className="acp-new-form__actions">
            <button type="submit" className="acp-btn acp-btn--primary">Crear</button>
            <button type="button" className="acp-btn acp-btn--ghost" onClick={() => { setShowNewCatForm(false); setNewCatName(''); setNewCatImage(null); setNewCatImagePreview(''); }}>
              Cancelar
            </button>
          </div>
        </form>
      )}

      {/* Lista de categorías */}
      <div className="acp__list">
        {categories
          .filter((cat) => {
            const q = search.trim().toLowerCase();
            if (!q) return true;
            return (
              cat.name.toLowerCase().includes(q) ||
              cat.subcategories?.some((s) => s.name.toLowerCase().includes(q))
            );
          })
          .sort((a, b) => a.name.localeCompare(b.name, 'es'))
          .map((cat) => (
          <div key={cat.id} className="acp-cat">

            {/* Fila de categoría */}
            <div className="acp-cat__row">
              {editingCatId === cat.id ? (
                <>
                  <input
                    autoFocus
                    className="acp-input acp-input--inline"
                    value={editingCatName}
                    onChange={(e) => setEditingCatName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleRenameCat(cat.id); if (e.key === 'Escape') { setEditingCatId(null); setEditingCatImage(null); setEditingCatImagePreview(''); } }}
                  />
                  <label className="acp-img-upload acp-img-upload--sm">
                    {editingCatImagePreview
                      ? <img src={editingCatImagePreview} alt="preview" className="acp-img-upload__preview" />
                      : getCatImageSrc(cat)
                        ? <img src={getCatImageSrc(cat)} alt="actual" className="acp-img-upload__preview" />
                        : <span className="acp-img-upload__placeholder">+ Img</span>
                    }
                    <input
                      type="file"
                      accept="image/svg+xml,image/png,image/jpeg,image/webp"
                      style={{ display: 'none' }}
                      onChange={(e) => handleImageChange(e.target.files[0], setEditingCatImage, setEditingCatImagePreview)}
                    />
                  </label>
                  <button className="acp-btn acp-btn--primary acp-btn--sm" onClick={() => handleRenameCat(cat.id)}>Guardar</button>
                  <button className="acp-btn acp-btn--ghost acp-btn--sm" onClick={() => { setEditingCatId(null); setEditingCatImage(null); setEditingCatImagePreview(''); }}>Cancelar</button>
                </>
              ) : (
                <>
                  <CategoryIcon slug={cat.slug} imageUrl={getCatImageSrc(cat)} size="sm" className="acp-cat__img" />
                  <span className="acp-cat__name">{fixEncoding(cat.name)}</span>
                  <span className="acp-cat__count">{cat.subcategories?.length || 0} subcategorías</span>
                  <div className="acp-cat__actions">
                    <button className="acp-btn acp-btn--outline acp-btn--sm" onClick={() => { setEditingCatId(cat.id); setEditingCatName(cat.name); setEditingCatImage(null); setEditingCatImagePreview(''); }}>
                      Editar
                    </button>
                    <button className="acp-btn acp-btn--ghost acp-btn--sm" onClick={() => { setAddingSubToCatId(cat.id); setNewSubName(''); }}>
                      + Subcategoría
                    </button>
                    <button className="acp-btn acp-btn--danger acp-btn--sm" onClick={() => handleDeleteCat(cat.id, cat.name)}>
                      Eliminar
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Formulario nueva subcategoría */}
            {addingSubToCatId === cat.id && (
              <form className="acp-sub-form" onSubmit={(e) => handleCreateSub(e, cat.id)}>
                <input
                  autoFocus
                  className="acp-input acp-input--sm"
                  placeholder="Nombre de la subcategoría"
                  value={newSubName}
                  onChange={(e) => setNewSubName(e.target.value)}
                />
                <button type="submit" className="acp-btn acp-btn--primary acp-btn--sm">Crear</button>
                <button type="button" className="acp-btn acp-btn--ghost acp-btn--sm" onClick={() => setAddingSubToCatId(null)}>
                  Cancelar
                </button>
              </form>
            )}

            {/* Subcategorías */}
            {cat.subcategories?.length > 0 && (
              <ul className="acp-subs">
                {cat.subcategories.sort((a, b) => a.name.localeCompare(b.name, 'es')).map((sub) => (
                  <li key={sub.id} className="acp-sub">
                    {editingSubId === sub.id ? (
                      <>
                        <input
                          autoFocus
                          className="acp-input acp-input--inline acp-input--sm"
                          value={editingSubName}
                          onChange={(e) => setEditingSubName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleRenameSub(sub.id); if (e.key === 'Escape') setEditingSubId(null); }}
                        />
                        <button className="acp-btn acp-btn--primary acp-btn--sm" onClick={() => handleRenameSub(sub.id)}>Guardar</button>
                        <button className="acp-btn acp-btn--ghost acp-btn--sm"   onClick={() => setEditingSubId(null)}>Cancelar</button>
                      </>
                    ) : (
                      <>
                        <span className="acp-sub__name">{fixEncoding(sub.name)}</span>
                        <div className="acp-sub__actions">
                          <button className="acp-btn acp-btn--outline acp-btn--sm" onClick={() => { setEditingSubId(sub.id); setEditingSubName(sub.name); }}>
                            Renombrar
                          </button>
                          <button className="acp-btn acp-btn--danger acp-btn--sm" onClick={() => handleDeleteSub(sub.id, sub.name)}>
                            Eliminar
                          </button>
                        </div>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}

          </div>
        ))}
      </div>

      {/* Modal de reubicación */}
      {reubicateModal && (
        <div className="acp-overlay" onClick={() => !reubicating && setReubicateModal(null)}>
          <div className="acp-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="acp-modal__title">Reubicar productos antes de eliminar</h3>
            <p className="acp-modal__body">
              La {reubicateModal.type === 'cat' ? 'categoría' : 'subcategoría'}{' '}
              <strong>"{reubicateModal.name}"</strong> tiene{' '}
              <strong>{reubicateModal.productCount} producto{reubicateModal.productCount !== 1 ? 's' : ''}</strong> asignados.
              Elige dónde moverlos antes de eliminarla.
            </p>

            <label className="acp-modal__label">
              {reubicateModal.type === 'cat' ? 'Categoría destino' : 'Subcategoría destino'}
            </label>
            <select
              className="acp-input"
              value={reubicateTarget}
              onChange={(e) => setReubicateTarget(e.target.value)}
            >
              <option value="">Seleccionar destino...</option>
              {destinationOptions().map((opt) =>
                reubicateModal.type === 'cat'
                  ? <option key={opt.id} value={opt.id}>{fixEncoding(opt.name)}</option>
                  : <option key={opt.id} value={opt.id}>{opt.label}</option>
              )}
            </select>

            <div className="acp-modal__actions">
              <button
                className="acp-btn acp-btn--ghost"
                onClick={() => setReubicateModal(null)}
                disabled={reubicating}
              >
                Cancelar
              </button>
              <button
                className="acp-btn acp-btn--danger"
                onClick={handleReubicateAndDelete}
                disabled={!reubicateTarget || reubicating}
              >
                {reubicating ? 'Moviendo...' : 'Mover y eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCategoriesPage;
